import { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Spade, Save, RefreshCw, Power, PowerOff, RotateCcw } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Switch } from '../../components/ui/switch';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

/**
 * ThreeCardPokerConfigPanel — admin controls for the in-house 3-Card Poker
 * table. Backs GET/PUT `/api/admin/tcp/config`. Setting `enabled=false`
 * silently closes the table (players see "table on a break").
 */
export const ThreeCardPokerConfigPanel = () => {
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [cfg, setCfg] = useState(null);
    const [edited, setEdited] = useState(null);
    const [defaults, setDefaults] = useState(null);

    const load = async () => {
        setLoading(true);
        try {
            const { data } = await axios.get(`${API}/admin/tcp/config`, { withCredentials: true });
            const { defaults: d, ...rest } = data;
            setCfg(rest);
            setEdited(rest);
            setDefaults(d);
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Failed to load 3-Card Poker config');
        } finally { setLoading(false); }
    };

    useEffect(() => { load(); }, []);

    const isDirty = useMemo(() => JSON.stringify(cfg) !== JSON.stringify(edited), [cfg, edited]);

    const save = async () => {
        if (!edited) return;
        setSaving(true);
        try {
            await axios.put(`${API}/admin/tcp/config`, edited, { withCredentials: true });
            toast.success('3-Card Poker config saved');
            await load();
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Save failed');
        } finally { setSaving(false); }
    };

    const resetDefaults = () => {
        if (!defaults) return;
        const { ...rest } = defaults;
        setEdited(rest);
        toast.message('Loaded defaults — press Save to apply');
    };

    if (!edited) {
        return <div className="card-dark p-5 text-[#A0AAB2] text-xs" data-testid="tcp-config-loading">Loading 3-Card Poker config…</div>;
    }

    const setField = (k, v) => setEdited((e) => ({ ...e, [k]: v }));
    const setPP = (k, v) => setEdited((e) => ({ ...e, pair_plus: { ...e.pair_plus, [k]: Number(v) } }));
    const setAB = (k, v) => setEdited((e) => ({ ...e, ante_bonus: { ...e.ante_bonus, [k]: Number(v) } }));

    return (
        <div className="card-dark p-5 space-y-5" data-testid="tcp-config-card">
            <div className="flex items-center justify-between flex-wrap gap-2">
                <div className="flex items-center gap-2">
                    <Spade className="w-5 h-5 text-[#00E1FF]" />
                    <h3 className="text-sm uppercase font-bold tracking-wide text-white">3-Card Poker Config</h3>
                </div>
                <div className="flex gap-2">
                    <button onClick={load} disabled={loading} className="text-xs text-[#A0AAB2] hover:text-white flex items-center gap-1">
                        <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} />Reload
                    </button>
                    <button onClick={resetDefaults} className="text-xs text-[#A0AAB2] hover:text-white flex items-center gap-1" data-testid="tcp-config-defaults">
                        <RotateCcw className="w-3.5 h-3.5" />Load defaults
                    </button>
                </div>
            </div>

            {/* Master enable */}
            <div className="flex items-center justify-between rounded-lg border border-white/10 bg-[#0e0e0e] p-3">
                <div className="flex items-center gap-2">
                    {edited.enabled ? <Power className="w-4 h-4 text-[#39FF14]" /> : <PowerOff className="w-4 h-4 text-[#FF3B30]" />}
                    <div>
                        <p className="text-sm font-bold text-white">Table {edited.enabled ? 'open' : 'closed'}</p>
                        <p className="text-[11px] text-[#A0AAB2]">When closed, players see a "table on a break" screen. All in-progress rounds still resolve normally.</p>
                    </div>
                </div>
                <Switch checked={edited.enabled} onCheckedChange={(v) => setField('enabled', v)} data-testid="tcp-config-enabled" />
            </div>

            {/* Bet limits */}
            <div className="grid grid-cols-2 gap-3">
                <div>
                    <Label className="text-[10px] uppercase tracking-widest text-[#A0AAB2]">Min bet</Label>
                    <Input type="number" step="0.25" min="0.25" value={edited.min_bet} onChange={(e) => setField('min_bet', Number(e.target.value))} className="mt-1" data-testid="tcp-config-min-bet" />
                </div>
                <div>
                    <Label className="text-[10px] uppercase tracking-widest text-[#A0AAB2]">Max bet</Label>
                    <Input type="number" step="1" min="1" value={edited.max_bet} onChange={(e) => setField('max_bet', Number(e.target.value))} className="mt-1" data-testid="tcp-config-max-bet" />
                </div>
            </div>

            {/* Pair Plus paytable */}
            <div>
                <p className="text-[10px] uppercase tracking-widest text-[#FF007F] font-bold mb-2">Pair Plus paytable (x:1)</p>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                    {[
                        ['mini_royal', 'Mini Royal'],
                        ['straight_flush', 'Straight Flush'],
                        ['three_of_kind', 'Three of a Kind'],
                        ['straight', 'Straight'],
                        ['flush', 'Flush'],
                        ['pair', 'Pair'],
                    ].map(([k, label]) => (
                        <div key={k}>
                            <Label className="text-[10px] text-[#A0AAB2]">{label}</Label>
                            <Input type="number" step="1" min="0" value={edited.pair_plus[k]} onChange={(e) => setPP(k, e.target.value)} className="mt-1" data-testid={`tcp-config-pp-${k}`} />
                        </div>
                    ))}
                </div>
            </div>

            {/* Ante Bonus paytable */}
            <div>
                <p className="text-[10px] uppercase tracking-widest text-[#FFD700] font-bold mb-2">Ante Bonus paytable (x:1)</p>
                <div className="grid grid-cols-3 gap-2">
                    {[
                        ['straight_flush', 'Straight Flush'],
                        ['three_of_kind', 'Three of a Kind'],
                        ['straight', 'Straight'],
                    ].map(([k, label]) => (
                        <div key={k}>
                            <Label className="text-[10px] text-[#A0AAB2]">{label}</Label>
                            <Input type="number" step="1" min="0" value={edited.ante_bonus[k]} onChange={(e) => setAB(k, e.target.value)} className="mt-1" data-testid={`tcp-config-ab-${k}`} />
                        </div>
                    ))}
                </div>
            </div>

            {/* Client toggles */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <div className="flex items-center justify-between rounded-lg border border-white/10 bg-[#0e0e0e] p-2.5">
                    <Label className="text-xs text-white">Sound effects</Label>
                    <Switch checked={edited.sounds_enabled} onCheckedChange={(v) => setField('sounds_enabled', v)} data-testid="tcp-config-sounds" />
                </div>
                <div className="flex items-center justify-between rounded-lg border border-white/10 bg-[#0e0e0e] p-2.5">
                    <Label className="text-xs text-white">Animations</Label>
                    <Switch checked={edited.animations_enabled} onCheckedChange={(v) => setField('animations_enabled', v)} data-testid="tcp-config-animations" />
                </div>
                <div>
                    <Label className="text-[10px] uppercase tracking-widest text-[#A0AAB2]">Auto deal delay (ms)</Label>
                    <Input type="number" step="100" min="0" value={edited.auto_deal_delay_ms} onChange={(e) => setField('auto_deal_delay_ms', Number(e.target.value))} className="mt-1" data-testid="tcp-config-auto-delay" />
                </div>
            </div>

            <div className="flex justify-end">
                <Button
                    onClick={save}
                    disabled={!isDirty || saving}
                    className="bg-[#00E1FF] hover:bg-[#00E1FF]/90 text-black font-black"
                    data-testid="tcp-config-save"
                >
                    <Save className="w-4 h-4 mr-1" />
                    {saving ? 'Saving…' : isDirty ? 'Save changes' : 'Saved'}
                </Button>
            </div>
        </div>
    );
};

export default ThreeCardPokerConfigPanel;
